import React from 'react';
import PropTypes from 'prop-types';
import {
  Line,
  Area,
  Vertex,
  Item,
  Group
} from './export';
import { MODE_DRAWING_SQUARE, UPDATE_DRAWING_SQUARE } from '../../constants';

function SquareDragWhiteFill({ layerID, vertices }, { store }) {
  const plannerState = store.getState().get('react-planner');
  const mode = plannerState.get('mode');
  if (mode !== MODE_DRAWING_SQUARE && mode !== UPDATE_DRAWING_SQUARE) {
    return null;
  }

  const draggingSupport = plannerState.get('draggingSupport');
  if (!draggingSupport) return null;

  const cornerIds = draggingSupport.get('cornerVertexIDs');
  if (!cornerIds || cornerIds.size !== 4) return null;
  if (String(draggingSupport.get('layerID')) !== String(layerID)) return null;

  const corners = cornerIds
    .map((id) => vertices.get(id))
    .filter(Boolean)
    .toArray();

  if (corners.length !== 4) return null;

  const xs = corners.map((v) => v.x);
  const ys = corners.map((v) => v.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const w = maxX - minX;
  const h = maxY - minY;
  if (w < 1 || h < 1) return null;

  return (
    <rect
      x={minX}
      y={minY}
      width={w}
      height={h}
      fill="#ffffff"
      fillOpacity={1}
      stroke="none"
      pointerEvents="none"
    />
  );
}

SquareDragWhiteFill.contextTypes = {
  store: PropTypes.object.isRequired,
};

export default function Layer({ layer, scene, catalog }) {
  let { unit, groups } = scene;
  let { lines, areas, vertices, holes, id: layerID, items, opacity } = layer;

  const sortedAreas = areas
    .valueSeq()
    .sort((a, b) => polygonAreaFromVertices(b, layer) - polygonAreaFromVertices(a, layer))
    .toArray();

  return (
    <g opacity={opacity}>
      <SquareDragWhiteFill layerID={layerID} vertices={vertices} />
      {
        sortedAreas.map(area =>
          <Area key={area.id} layer={layer} area={area} unit={unit} catalog={catalog} labelsOnly={false} />)
      }
      {
        lines.valueSeq().map(line =>
          <Line key={line.id} layer={layer} line={line} scene={scene} catalog={catalog} />)
      }
      {
        items.valueSeq().map(item =>
          <Item key={item.id} layer={layer} item={item} scene={scene} catalog={catalog} />)
      }
      {
        vertices
          .valueSeq()
          .map(vertex => <Vertex key={vertex.id} layer={layer} vertex={vertex} />)
      }
      {
        groups
          .valueSeq()
          .filter(g => g.hasIn(['elements', layerID]) && g.get('selected'))
          .map(group => <Group key={group.get('id')} layer={layer} group={group} scene={scene} catalog={catalog} />)
      }
      {
        sortedAreas.map(area =>
          <Area key={`lbl-${area.id}`} layer={layer} area={area} unit={unit} catalog={catalog} labelsOnly={true} />)
      }
    </g>
  );
}

function polygonAreaFromVertices(area, layer) {
  const pts = area.vertices
    .toArray()
    .map((id) => layer.vertices.get(id))
    .filter(Boolean)
    .map((v) => [v.x, v.y]);
  if (pts.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    sum += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
  }
  return Math.abs(sum / 2);
}

Layer.propTypes = {
  layer: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

Layer.contextTypes = {
  store: PropTypes.object.isRequired,
};
